/**
 * Centralized auth-intent storage, retrieval, and resolution.
 *
 * When a guest user triggers an auth-gated action, the app stores an
 * "auth intent" describing what to resume after authentication. This
 * module owns the full lifecycle: write, read, clear, and resolve to
 * a navigation target.
 *
 * Storage uses a single JSON blob in sessionStorage under the key
 * `meetball_auth_intent`. On native, also writes to Capacitor
 * Preferences for durability across app-kill during auth.
 *
 * `create_pending_question` is explicitly excluded — it has its own
 * consumption path in useClaimPendingQuestions.
 */

import { routes } from '@routes';
import { getCommunityPath, getQuestionPath } from '@utils/slugHelpers';
import { CommunityTab } from '@screens/CommunityDetail.tabs';
import type { IntentDraft } from '@lib/overlay/intents';
import type { CreateEventContextType } from '@lib/analytics-events';
import { isNativePlatform } from '@lib/platform';
import { logger } from '@utils/logger';
import { AUTH_FLOW_TTL_MS } from '@utils/authFlowTtl';
import {
  MAGIC_LINK_MIRROR_KEYS,
  clearMagicLinkMirror,
  readMagicLinkMirror,
  writeMagicLinkPayloadMirror,
} from '@utils/magicLinkMirror';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Auth-gated action kinds handled by the intent system. */
export const AuthIntent = {
  CHECK_IN: 'CHECK_IN',
  CREATE_EVENT: 'CREATE_EVENT',
  SAVE_EVENT: 'SAVE_EVENT',
  GOING_EVENT: 'GOING_EVENT',
  CREATE_COMMUNITY: 'CREATE_COMMUNITY',
  JOIN_COMMUNITY: 'JOIN_COMMUNITY',
  ACCEPT_SPACE_INVITE: 'ACCEPT_SPACE_INVITE',
  VOTE_POLL: 'VOTE_POLL',
  LIVE_POLL_ANSWERS: 'LIVE_POLL_ANSWERS',
} as const;

export type AuthIntentKind = (typeof AuthIntent)[keyof typeof AuthIntent];

/** Discriminated union — parameterized kinds carry their required data. */
export type AuthIntentPayload =
  | { kind: typeof AuthIntent.CHECK_IN }
  | { kind: typeof AuthIntent.CREATE_EVENT }
  | { kind: typeof AuthIntent.SAVE_EVENT }
  // GOING_EVENT carries `eventId`, which drives the attend POST and the post-auth landing on the
  // event page. `spaceSlug` is retained on the payload (set by the guest page) but routing no longer
  // uses it: after the auto-join we land back on the event, not the host space.
  // `source` is arrival provenance (currently only 'qr_event' — the event-QR welcome flow);
  // executeGoingEvent forwards it to the "You're going" celebration marker for analytics only
  // (design §4.2) — every post-auth attend queues the celebration regardless of source.
  | {
      kind: typeof AuthIntent.GOING_EVENT;
      eventId: string;
      spaceSlug?: string | null;
      source?: string;
    }
  | { kind: typeof AuthIntent.CREATE_COMMUNITY }
  // `fromWelcome` marks signups that began on a space welcome screen. It is a provenance hint only:
  // shouldSkipBrandIntro reads it to skip the brand-intro carousel, since the welcome already served
  // that context. SpaceWelcomeV2 sets it directly (Mode B); CommunityDetail derives it via
  // buildJoinCommunityIntent → hasSeenWelcome (Mode A). Non-welcome producers (EventGuestPage,
  // Events) omit it, so plain joins keep the intro.
  // It is deliberately NOT forwarded into navigation state. It used to be, so CommunityDetail
  // could suppress its joined-drawer — but provenance is not the same question as "did a
  // celebration actually show", and that predicate silently swallowed the confirmation for
  // every welcome-originated sign-in and skipped signup. The destination screen now queues its
  // own success drawer behind whatever celebration is live by checking live celebration
  // busy-state itself — no provenance or intent signal needs to travel in navigation state.
  // `inviteToken` (design §5): a guest joining an unlisted space signs up
  // mid-flow — the token must survive the auth round-trip or the freshly
  // signed-up invitee lands on the "requires an invite link" gate.
  | {
      kind: typeof AuthIntent.JOIN_COMMUNITY;
      communitySlug: string;
      fromWelcome?: boolean;
      inviteToken?: string;
    }
  | { kind: typeof AuthIntent.ACCEPT_SPACE_INVITE; communitySlug: string; invitationToken: string }
  // A signed-out answer to a poll (TECH-197 § Voting "Signed out"; polls CONTRACT § 6): cast after
  // sign-in by executePostAuthIntent. The first intent to carry a list (1-10 option ids, as the
  // backend's `VotePollIntent`, whose camelCase aliases these names are).
  | { kind: typeof AuthIntent.VOTE_POLL; questionId: string; optionIds: string[] }
  // A live poll's guest answers (TECH-867 § Answering "Attach"; polls CONTRACT § 6): written only when the guest
  // submits the join card's email (the sign-in action). After sign-in, on any device, executePostAuthIntent lands
  // on `/live/{liveCode}` and redeems `guestClaim` (`POST /live/{code}/claim`), then joins by type. `guestClaim`
  // is a capability bound server-side to the email that sign-in went to (U2), so the strip drops the intent whole.
  | { kind: typeof AuthIntent.LIVE_POLL_ANSWERS; liveCode: string; guestClaim: string };

/**
 * Whether the signup brand-intro carousel (OnboardingSlides) should be skipped for a given intent.
 *
 * Skipped only for guests who arrived through a curated entry that already gave them enough context,
 * so the generic carousel would be friction on the action they asked for:
 *   - GOING_EVENT: signed up to join a specific event (already engaged via the event page).
 *   - CREATE_EVENT: signed up to add an event, from a Space calendar or the Events feed (TECH-847).
 *   - JOIN_COMMUNITY with `fromWelcome`: signed up from a space welcome screen (SpaceWelcomeV2).
 *     Plain JOIN_COMMUNITY (joining from a list/detail page) still gets the intro.
 * No stored intent, or any other kind, keeps the intro.
 *
 * Pure (reads only the payload), so it is safe to call from a render-phase lazy initializer.
 * If a second provenance source ever needs to skip the intro, migrate `fromWelcome?: boolean` to a
 * `source?: 'welcome' | 'list' | 'detail'` discriminator rather than adding more optional booleans.
 */
export function shouldSkipBrandIntro(intent: AuthIntentPayload | null): boolean {
  if (!intent) return false;
  switch (intent.kind) {
    // VOTE_POLL: an answer saved signed out; the poll is the context, as an event is for GOING_EVENT.
    // LIVE_POLL_ANSWERS: signed up from a live poll's join card; the room is the context.
    case 'GOING_EVENT':
    case 'VOTE_POLL':
    case 'LIVE_POLL_ANSWERS':
    case 'CREATE_EVENT':
      return true;
    case 'JOIN_COMMUNITY':
      return intent.fromWelcome === true;
    case 'CHECK_IN':
    case 'SAVE_EVENT':
    case 'CREATE_COMMUNITY':
    case 'ACCEPT_SPACE_INVITE':
      return false;
    default: {
      // Exhaustiveness guard (mirrors resolveAuthIntent): a new AuthIntent kind won't compile until
      // it is explicitly classified above. `false` is the safe runtime fallback.
      const _exhaustive: never = intent;
      logger.error('[authIntent] Unhandled intent kind in shouldSkipBrandIntro:', _exhaustive);
      return false;
    }
  }
}

/** Envelope stored in sessionStorage / Capacitor Preferences. */
interface StoredAuthIntent {
  payload: AuthIntentPayload;
  createdAt: number;
}

/** Navigation target returned by `resolveAuthIntent`. */
export interface ResolvedAuthIntent {
  path: string;
  /** Overlay intents to post (in order) immediately before navigating. */
  intents?: IntentDraft[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'meetball_auth_intent';

/**
 * The sessionStorage copy's TTL: 30 minutes, matching getStoredRedirectPath, the
 * cross-tab mirror, and the life of the token a magic link is minted with.
 */
const TTL_MS = AUTH_FLOW_TTL_MS;

/**
 * The DURABLE native copy's TTL, deliberately shorter than `TTL_MS`.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: `TTL_MS` is 30 minutes because that is the
 * length of an email round trip -- the life of the token a magic link is minted
 * with. Native has no such round trip: the door and its localStorage mirrors are
 * web surfaces, and `useNativePostAuth` runs off an isAuthenticated transition
 * instead. What this copy has to span is one in-app-browser sign-in, including a
 * WebView kill during it -- which is the whole reason it is Preferences and not
 * sessionStorage, and also why it is the one copy that outlives the process.
 * 10 minutes covers that with room to spare, and keeps a durable intent --
 * JOIN_COMMUNITY joins, GOING_EVENT POSTs an attend -- from sitting on the device
 * for half an hour waiting for a sign-in that has nothing to do with it.
 */
export const NATIVE_INTENT_TTL_MS = 10 * 60 * 1000;

const VALID_KINDS = new Set<string>(Object.values(AuthIntent));

/** A poll has at most 10 options, so an answer names at most 10 (CONTRACT § 6). */
const MAX_VOTE_POLL_OPTION_IDS = 10;

/**
 * AIDEV-NOTE [MEETBALL magic-link]: cross-tab mirror of the intent, twin of
 * `mirrorRedirectPathForMagicLink` in @utils/auth -- see the long note there for
 * why sessionStorage alone loses this stash (the link opens in a new tab, often
 * another browser or device). Mirror and sessionStorage now share one 30-minute
 * TTL (@utils/authFlowTtl), the life of the token that resumes the flow.
 * Written only when the /auth door flow is entered; cleared by clearAuthIntent.
 * Multi-tab caveat: last writer wins, accepted for this flow.
 */
export const MAGIC_LINK_INTENT_STORAGE_KEY = MAGIC_LINK_MIRROR_KEYS.intent;

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

function isValidKind(value: unknown): value is AuthIntentKind {
  return typeof value === 'string' && VALID_KINDS.has(value);
}

/**
 * Runtime type guard for a deserialized payload.
 * Checks `kind` and that parameterized kinds carry required fields.
 *
 * EXPORTED because a payload can now arrive from a third place: the server's
 * magic-link flow-context endpoint, whose body is whatever some earlier browsing
 * context posted. It is validated with THIS predicate rather than a second one
 * written next to the fetch, so a new intent kind cannot be accepted by one
 * reader and rejected by the other.
 */
export function isValidAuthIntentPayload(value: unknown): value is AuthIntentPayload {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  if (!isValidKind(obj.kind)) return false;

  if (obj.kind === 'JOIN_COMMUNITY') {
    if (typeof obj.communitySlug !== 'string' || obj.communitySlug.length === 0) return false;
    if (obj.fromWelcome !== undefined && typeof obj.fromWelcome !== 'boolean') return false;
    return obj.inviteToken === undefined || typeof obj.inviteToken === 'string';
  }
  if (obj.kind === 'ACCEPT_SPACE_INVITE') {
    return (
      typeof obj.communitySlug === 'string' &&
      obj.communitySlug.length > 0 &&
      typeof obj.invitationToken === 'string' &&
      obj.invitationToken.length > 0
    );
  }
  if (obj.kind === 'GOING_EVENT') {
    // eventId is required (drives the attend POST + the event-page landing); spaceSlug is optional
    // (spaceless events omit it) but, when present, must be a non-empty string.
    if (typeof obj.eventId !== 'string' || obj.eventId.length === 0) return false;
    if (obj.spaceSlug != null && (typeof obj.spaceSlug !== 'string' || obj.spaceSlug.length === 0))
      return false;
    if (obj.source !== undefined && typeof obj.source !== 'string') return false;
    return true;
  }
  if (obj.kind === 'VOTE_POLL') {
    // As the backend's VotePollIntent: a question id and 1-10 option ids, every one a non-empty
    // string (an id list the server would refuse drops the whole flow context there).
    if (typeof obj.questionId !== 'string' || obj.questionId.length === 0) return false;
    return (
      Array.isArray(obj.optionIds) &&
      obj.optionIds.length > 0 &&
      obj.optionIds.length <= MAX_VOTE_POLL_OPTION_IDS &&
      obj.optionIds.every(id => typeof id === 'string' && id.length > 0)
    );
  }
  if (obj.kind === 'LIVE_POLL_ANSWERS') {
    // As the backend's LivePollAnswersIntent: both fields required, non-empty strings.
    return (
      typeof obj.liveCode === 'string' &&
      obj.liveCode.length > 0 &&
      typeof obj.guestClaim === 'string' &&
      obj.guestClaim.length > 0
    );
  }
  return true;
}

// ---------------------------------------------------------------------------
// Write
// ---------------------------------------------------------------------------

/**
 * Store an auth intent. Writes a single JSON blob to sessionStorage.
 * On native, also writes to Capacitor Preferences for durability across app-kill.
 */
export function setAuthIntent(payload: AuthIntentPayload): void {
  const envelope: StoredAuthIntent = { payload, createdAt: Date.now() };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(envelope));
  } catch (err) {
    logger.error('[authIntent] Failed to write sessionStorage:', err);
  }

  if (isNativePlatform()) {
    void writeNativePreferences(envelope);
  }
}

// ---------------------------------------------------------------------------
// Read
// ---------------------------------------------------------------------------

/**
 * Copies the current sessionStorage intent into localStorage so it survives the
 * magic link opening in a different tab. Call ONLY when entering the door flow.
 */
/**
 * The same intent with every CAPABILITY taken out of it.
 *
 * `inviteToken` on JOIN_COMMUNITY and `invitationToken` on ACCEPT_SPACE_INVITE are
 * the two fields in this union that are not descriptions of an action but the
 * AUTHORITY to perform one: a guest holding either can join a space they were never
 * listed for. Everything else here names a public thing (an event id, a space slug)
 * and travels unchanged.
 *
 * ACCEPT_SPACE_INVITE cannot survive the removal -- `invitationToken` is required on
 * that kind, and a payload without it would fail `isValidAuthIntentPayload` and be
 * dropped whole -- so it DEGRADES to a plain JOIN_COMMUNITY for the same space.
 * That keeps the destination and the intent to join while losing the authority,
 * which is exactly the trade: the visitor lands on the space and sees the ordinary
 * "this space needs an invite" surface instead of silently being let in on somebody
 * else's invitation.
 *
 * Called by `/auth`'s send when the address being sent to is not the address this
 * tab first sent to -- see `@lib/magicLink/firstSendAddress` for why, and for the
 * typo residual it accepts.
 */
export function stripIntentCapabilities(
  intent: AuthIntentPayload | null,
): AuthIntentPayload | null {
  if (!intent) {
    return null;
  }
  // A SWITCH with an exhaustiveness guard, not an if-chain ending in `return intent`.
  //
  // AIDEV-NOTE [MEETBALL magic-link]: the fall-through form was one added kind away
  // from a silent leak -- a new payload carrying a token would have matched none of
  // the branches and been passed through whole, to a second address, with nothing
  // failing anywhere. Same shape as `shouldSkipBrandIntro` above, for the same
  // reason: a new kind does not compile until somebody has classified it.
  switch (intent.kind) {
    case 'JOIN_COMMUNITY': {
      // Falsy, not just undefined: `inviteToken: ''` is not a capability (a join
      // would fail on it), so treating one as something to remove rebuilds the object
      // for nothing -- and the door reads a rebuild as "a capability was held back"
      // and says so to the visitor.
      if (!intent.inviteToken) {
        return intent;
      }
      const { inviteToken: _inviteToken, ...rest } = intent;
      return rest;
    }
    case 'ACCEPT_SPACE_INVITE': {
      // Same rule: an empty `invitationToken` grants nothing, so degrading the intent
      // (and announcing it) would cost the visitor their join for no security gain.
      // `isValidAuthIntentPayload` already rejects that shape, so this is the
      // belt-and-braces half of one rule rather than a second rule.
      if (!intent.invitationToken) {
        return intent;
      }
      return { kind: AuthIntent.JOIN_COMMUNITY, communitySlug: intent.communitySlug };
    }
    // `guestClaim` is a capability AND required (as ACCEPT_SPACE_INVITE's token), and `liveCode` itself stands
    // in for an unlisted join Space's invite (CONTRACT § 6, § 9.2): nothing safe is left, so it is dropped whole.
    case 'LIVE_POLL_ANSWERS':
      return null;
    // Nothing to take: an event id and a space slug name PUBLIC things, a poll answer names a
    // post and its options (no authority: the vote's own membership rules decide), and the
    // remaining kinds carry no fields at all.
    case 'VOTE_POLL':
    case 'CHECK_IN':
    case 'CREATE_EVENT':
    case 'SAVE_EVENT':
    case 'GOING_EVENT':
    case 'CREATE_COMMUNITY':
      return intent;
    default: {
      const _exhaustive: never = intent;
      logger.error('[authIntent] Unhandled intent kind in stripIntentCapabilities:', _exhaustive);
      // A kind nobody has classified is treated as capability-bearing: dropping it is
      // the only answer that cannot leak one.
      return null;
    }
  }
}

export function mirrorAuthIntentForMagicLink(
  sanitize?: (intent: AuthIntentPayload | null) => AuthIntentPayload | null,
): void {
  // Deliberately the sessionStorage read, not readAuthIntent(): mirroring the
  // mirror back onto itself would only refresh its own timestamp.
  //
  // An ABSENT intent CLEARS the mirror rather than leaving it. Intents have side
  // effects -- JOIN_COMMUNITY joins, GOING_EVENT POSTs an attend -- and the
  // magic-link tab's own sessionStorage is empty by construction, so a survivor
  // from an abandoned door entry would be the only thing
  // `hydrateAuthIntentFromMagicLinkMirror` found on the next link opened in this
  // profile. Entering the door is where the mirror is made to describe THIS
  // flow, including when this flow carries no intent.
  // `sanitize` is how the door mirrors a REDUCED copy for a send to a second
  // address: the mirror is read back by whoever redeems a link in this profile, so
  // it must not re-arm an authority the upload just refused to carry. The door's
  // mount passes it too once a send from this tab has gone out stripped (the
  // send-elsewhere signal in `firstSendAddress.ts`: a send to an address other than
  // the bound one, or any send from a tab whose storage refused the binding), and never
  // on "Wrong address?" alone, which sends nobody anything. Otherwise it is absent on a
  // door arrival, where the mirror describes this tab's own flow.
  const intent = readSessionAuthIntent();
  writeMagicLinkPayloadMirror(MAGIC_LINK_MIRROR_KEYS.intent, sanitize ? sanitize(intent) : intent);
}

/**
 * Copies the cross-tab mirror into THIS tab's sessionStorage, and drops the
 * mirror. Called once, by `/auth/link`, at the moment a magic-link redemption is
 * actually starting.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: this replaces a fallback that used to live
 * inside `readAuthIntent`, which meant EVERY sign-in in the app read it. Intents
 * have side effects -- JOIN_COMMUNITY joins, GOING_EVENT POSTs an attend -- and
 * two sign-in paths documented elsewhere in this codebase never pass through the
 * door that refreshes the mirror: `useInvalidGrantHandler`'s full-login fallback
 * (useSignInOut.ts states that silent-recovery sign-ins must never be
 * door-routed) and every native sign-in. So guest A's abandoned "Join community
 * X" sat in localStorage for 30 minutes and executed under whoever signed in
 * next on the same browser profile, against A's invite token.
 *
 * Hydrating at `/auth/link` binds the value to the flow that wrote it without
 * needing a flow id: that screen is the only surface in the app that knows this
 * document is finishing a magic-link redemption, and the tab it runs in is the
 * same tab `/callback` returns to. Everything downstream reads sessionStorage,
 * exactly as it does on an ordinary sign-in.
 *
 * Returns whether anything was hydrated, and when it was, this tab's previous intent is gone:
 * the move is a REPLACEMENT, so a write storage refuses leaves no intent rather than the old
 * one. `/auth/link` moves the binding with the payload on this answer (#1274), and an old
 * intent left under the envelope's binding would travel whole to the envelope's address.
 */
export function hydrateAuthIntentFromMagicLinkMirror(): boolean {
  const mirrored = readMagicLinkMirror(MAGIC_LINK_MIRROR_KEYS.intent, isValidAuthIntentPayload);
  clearMagicLinkMirror(MAGIC_LINK_MIRROR_KEYS.intent);

  if (mirrored === null) {
    return false;
  }

  // The sessionStorage halves of these two are synchronous, and they are what every reader
  // here uses. On native each also touches Capacitor Preferences, un-awaited, so there the
  // removal only PRECEDES the write by call order (both go through the same dynamic import,
  // and their continuations run in the order they were queued). Left un-awaited because this
  // hydrate is synchronous for its one caller, and because native never reaches it: native
  // never routes a sign-in to the door (`shouldRouteToDoor`), so no envelope is ever written
  // for a native `/auth/link` to hydrate.
  clearAuthIntentKeepingMirror();
  setAuthIntent(mirrored);
  return true;
}

/**
 * Read the current auth intent. Returns null if absent, expired, or corrupt.
 *
 * sessionStorage ONLY. The cross-tab mirror reaches this value through
 * `hydrateAuthIntentFromMagicLinkMirror`, which `/auth/link` calls on the one
 * document that is provably finishing a magic-link flow. See that function for
 * why a fallback here was too wide.
 *
 * Does NOT clear after reading — caller decides when to clear.
 *
 * `maxAgeMs` narrows the window for a reader whose intent lives less long than this copy: a poll
 * answer saved signed out shows on native for `NATIVE_INTENT_TTL_MS` only (TECH-197 E5).
 */
export function readAuthIntent(maxAgeMs?: number): AuthIntentPayload | null {
  return readSessionAuthIntent(maxAgeMs);
}

/**
 * The intent a native sign-in replays (`useNativePostAuth`): as `readAuthIntent`, except that a
 * `VOTE_POLL` lives `NATIVE_INTENT_TTL_MS` there, the window its card shows the pick in (E5), so a
 * pick the card stopped showing is never cast later (FE-1d review).
 */
export function readAuthIntentForNativeReplay(): AuthIntentPayload | null {
  const intent = readSessionAuthIntent();
  return intent?.kind === AuthIntent.VOTE_POLL
    ? readSessionAuthIntent(NATIVE_INTENT_TTL_MS)
    : intent;
}

function readSessionAuthIntent(maxAgeMs?: number): AuthIntentPayload | null {
  return readAuthIntentOrUnreadable(maxAgeMs) ?? null;
}

/**
 * `readAuthIntent`, except that a storage read that THROWS answers `undefined` rather
 * than the `null` of a tab holding no intent. ONE read answers both questions, which is
 * the point: a second read asking "did that throw" can answer cleanly after the first one
 * threw, and the half it was asking about is then compared rather than cleared. For the
 * fail-closed strip only (`holdStashToRule` in `@lib/magicLink/capabilityStash`; F2 of
 * #1253's adversarial review), which clears what it could not read. Everything else reads
 * `readAuthIntent`, which is this `?? null`. A value that does not parse, an expired one
 * or another bundle's shape is no intent to any reader, so it is `null` here too.
 */
export function readAuthIntentOrUnreadable(
  maxAgeMs: number = TTL_MS,
): AuthIntentPayload | null | undefined {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
  } catch (err) {
    logger.warn('[authIntent] Failed to read from sessionStorage:', err);
    return undefined;
  }
  try {
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;

    const envelope = parsed as { payload?: unknown; createdAt?: unknown };
    if (typeof envelope.createdAt !== 'number') return null;
    if (Date.now() - envelope.createdAt >= Math.min(TTL_MS, maxAgeMs)) return null;
    if (!isValidAuthIntentPayload(envelope.payload)) return null;

    return envelope.payload;
  } catch (err) {
    logger.warn('[authIntent] Failed to read from sessionStorage:', err);
    return null;
  }
}

/**
 * Existence check, MIRROR INCLUDED.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: delegates to `readAuthIntent` rather than
 * re-reading sessionStorage. It used to do the latter and skip payload
 * validation "for efficiency", which made the two predicates disagree in exactly
 * the tab a magic link opens in: sessionStorage is empty there by definition
 * (that is why the mirror exists), so `readAuthIntent()` found the intent and
 * ran it while `hasAuthIntent()` reported false. Two live consumers read the
 * false one -- useSignInOut's `shouldPersist` and the `has_intended_action`
 * property on `auth_initiated`. This runs once per sign-in click, so the parse
 * is not worth a correctness split.
 */
export function hasAuthIntent(): boolean {
  return readAuthIntent() !== null;
}

// ---------------------------------------------------------------------------
// Clear
// ---------------------------------------------------------------------------

/**
 * Clear THIS TAB's auth intent and leave the cross-tab mirror exactly as it is:
 * sessionStorage, and on native the Preferences copy `setAuthIntent` writes beside
 * it (the tab's own durable copy, which `readNativeAuthIntent` hands the post-auth
 * path after an app kill), but never the localStorage envelope.
 *
 * For the door's fail-closed strip (`holdStashToRule` in `@lib/magicLink/capabilityStash`),
 * which runs while the visitor is still on the door, as often as not one line after a
 * send wrote the envelope for its own link. The envelope describes that LINK, and only
 * the door's mirror writers decide what it says: clearing it there erased what the
 * send had just written, and the link's recipient lost the destination and the plain
 * join along with the invite. `clearAuthIntent` below is this plus the mirror.
 */
export function clearAuthIntentKeepingMirror(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    logger.error('[authIntent] Failed to clear sessionStorage:', err);
  }

  if (isNativePlatform()) {
    void clearNativePreferences();
  }
}

/**
 * Clear the auth intent from all storage layers, the cross-tab localStorage
 * mirror included: consumers call this on consumption and on a successful
 * non-magic sign-in, and a surviving mirror would bleed into the next flow.
 */
export function clearAuthIntent(): void {
  clearAuthIntentKeepingMirror();
  clearMagicLinkMirror(MAGIC_LINK_MIRROR_KEYS.intent);
}

// ---------------------------------------------------------------------------
// Resolve
// ---------------------------------------------------------------------------

/**
 * Whether the guest left the Partners page of the same Space to sign up, judged by `redirectPath`
 * (what `signIn` stored for this flow). A guest who tapped Join on the Partners page's gate lands
 * back on Partners, where the newly visible cards are (TECH-86); the JOIN_COMMUNITY payload cannot
 * carry the tab itself, because the backend's magic-link flow context forbids extra intent fields.
 * Only Partners rides along: any other tab lands on Home as before, and the rest of the URL is
 * rebuilt from the payload.
 */
function leftPartnersTab(redirectPath: string | null, communitySlug: string): boolean {
  if (!redirectPath) return false;
  try {
    const url = new URL(redirectPath, 'https://meetball.invalid');
    const spacePath = getCommunityPath({ id: null, slug: communitySlug });
    return url.pathname === spacePath && url.searchParams.get('tab') === CommunityTab.Partners;
  } catch {
    return false;
  }
}

/**
 * `/communities/<slug>` (and its query) out of a stored redirect path, or null. Any single segment:
 * a slug or an id.
 */
function communityFromRedirectPath(
  redirectPath: string | null,
): { slug: string; invite: string | null } | null {
  if (!redirectPath) return null;
  try {
    const url = new URL(redirectPath, 'https://meetball.invalid');
    const match = /^\/communities\/([^/]+)\/?$/.exec(url.pathname);
    return match
      ? { slug: decodeURIComponent(match[1]), invite: url.searchParams.get('invite') }
      : null;
  } catch {
    return null;
  }
}

/** The query param that marks a signed-out invitee's Add Event return (see `leftViaAddEvent`). */
export const ADD_EVENT_RETURN_PARAM = 'addEvent';

/** The stored search with the Add Event marker added, for a signed-out invitee who tapped Add Event. */
export function withAddEventMarker(search: string): string {
  const params = new URLSearchParams(search);
  params.set(ADD_EVENT_RETURN_PARAM, '1');
  return `?${params.toString()}`;
}

/** Whether the stored redirect path is this Space's page with the Add Event marker. */
export function leftViaAddEvent(redirectPath: string | null, communitySlug: string): boolean {
  if (!redirectPath) return false;
  try {
    const url = new URL(redirectPath, 'https://meetball.invalid');
    return (
      url.pathname === getCommunityPath({ id: null, slug: communitySlug }) &&
      url.searchParams.get(ADD_EVENT_RETURN_PARAM) === '1'
    );
  } catch {
    return false;
  }
}

/**
 * For auth analytics: what a stored CREATE_EVENT intent says about where the person started.
 * A signed-out invitee's Add Event stores ACCEPT_SPACE_INVITE plus the Add Event marker instead.
 */
export function createEventAuthContext(
  intent: AuthIntentPayload | null,
  redirectPath: string | null,
): { action_type: 'create_event'; context_type: CreateEventContextType } | null {
  if (
    intent?.kind === AuthIntent.ACCEPT_SPACE_INVITE &&
    leftViaAddEvent(redirectPath, intent.communitySlug)
  ) {
    return { action_type: 'create_event', context_type: 'space_calendar' };
  }
  if (intent?.kind !== AuthIntent.CREATE_EVENT) return null;
  return {
    action_type: 'create_event',
    context_type: communityFromRedirectPath(redirectPath) ? 'space_calendar' : 'events_feed',
  };
}

/**
 * Pure function: given an intent payload and optional redirect path,
 * return the navigation target (path + intents to post before navigating).
 *
 * Does not call navigate() or touch storage — callers handle that.
 */
export function resolveAuthIntent(
  payload: AuthIntentPayload,
  redirectPath: string | null,
): ResolvedAuthIntent {
  switch (payload.kind) {
    case 'CHECK_IN':
      return { path: redirectPath ?? routes.events() };

    case 'CREATE_EVENT': {
      // A sign-in that started on a Space calendar lands back on it (TECH-847); the Space screen
      // consumes the intent, runs the join as a tap would, and opens the add-event drawer.
      const space = communityFromRedirectPath(redirectPath);
      if (!space) {
        return {
          path: `${routes.events()}?tab=events`,
          intents: [{ surface: 'create-event', payload: {} }],
        };
      }
      return {
        path: routes.community.details(
          { id: null, slug: space.slug },
          {
            returnTo: routes.events(),
            // `invite` rides along: on an unlisted Space it is the visitor's only proof of access.
            query: { tab: CommunityTab.Events, ...(space.invite ? { invite: space.invite } : {}) },
          },
        ),
        intents: [{ surface: 'create-event', payload: { community: space.slug } }],
      };
    }

    case 'SAVE_EVENT':
      return { path: redirectPath ?? routes.events() };

    case 'GOING_EVENT':
      // Land back on the event page (not the host space). The async attend + auto-join still runs
      // in executePostAuthIntent; this is the pure routing target. Prefer the stored redirect (the
      // exact event page the guest was on, slug preserved), else build it from the event id.
      return {
        path:
          redirectPath ??
          routes.event.details({ id: payload.eventId, slug: null }, { returnTo: routes.events() }),
      };

    case 'CREATE_COMMUNITY':
      return { path: routes.community.create() };

    case 'JOIN_COMMUNITY': {
      const query: Record<string, string> = {};
      if (payload.inviteToken) query.invite = payload.inviteToken;
      if (leftPartnersTab(redirectPath, payload.communitySlug)) query.tab = CommunityTab.Partners;
      return {
        path: routes.community.details(
          { id: null, slug: payload.communitySlug },
          {
            returnTo: routes.events(),
            query: Object.keys(query).length > 0 ? query : undefined,
          },
        ),
        // Routing only. `payload.fromWelcome` deliberately does NOT ride along: the destination
        // screen queues its own success drawer behind whatever celebration is live by checking
        // live celebration busy-state itself, rather than relying on a signal carried in the
        // intent. This function is pure routing and has no view of that.
        intents: [{ surface: 'community-join', payload: { community: payload.communitySlug } }],
      };
    }

    case 'VOTE_POLL':
      // The poll itself (the Post sheet); executePostAuthIntent casts the answer first.
      return { path: getQuestionPath({ id: payload.questionId, slug: null }) };

    case 'LIVE_POLL_ANSWERS':
      // The live link (the sheet, still waiting or on the open question); executePostAuthIntent claims first.
      return { path: `/live/${encodeURIComponent(payload.liveCode)}` };

    case 'ACCEPT_SPACE_INVITE':
      return {
        path: routes.community.details(
          { id: null, slug: payload.communitySlug },
          { returnTo: routes.events(), query: { invitation: payload.invitationToken } },
        ),
      };

    default: {
      const _exhaustive: never = payload;
      logger.error('[authIntent] Unhandled intent kind in resolveAuthIntent:', _exhaustive);
      return { path: routes.home() };
    }
  }
}

// ---------------------------------------------------------------------------
// Internal: Capacitor Preferences (native durable storage)
// ---------------------------------------------------------------------------

async function writeNativePreferences(envelope: StoredAuthIntent): Promise<void> {
  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.set({ key: STORAGE_KEY, value: JSON.stringify(envelope) });
  } catch (err) {
    logger.error('[authIntent] Failed to write native Preferences:', err);
  }
}

async function clearNativePreferences(): Promise<void> {
  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.remove({ key: STORAGE_KEY });
  } catch (err) {
    logger.error('[authIntent] Failed to clear native Preferences:', err);
  }
}

/**
 * Read auth intent from Capacitor Preferences (fallback when sessionStorage
 * is lost after app-kill during auth).
 *
 * Expires at `NATIVE_INTENT_TTL_MS`, NOT at the sessionStorage `TTL_MS`: this
 * copy survives an app kill, so its window is a durable one. See that constant.
 */
export async function readNativeAuthIntent(): Promise<AuthIntentPayload | null> {
  try {
    const { Preferences } = await import('@capacitor/preferences');
    const { value } = await Preferences.get({ key: STORAGE_KEY });
    if (!value) return null;

    const parsed: unknown = JSON.parse(value);
    if (typeof parsed !== 'object' || parsed === null) return null;

    const envelope = parsed as { payload?: unknown; createdAt?: unknown };
    if (typeof envelope.createdAt !== 'number') return null;
    if (Date.now() - envelope.createdAt >= NATIVE_INTENT_TTL_MS) return null;
    if (!isValidAuthIntentPayload(envelope.payload)) return null;

    return envelope.payload;
  } catch (err) {
    logger.error('[authIntent] Failed to read native Preferences:', err);
    return null;
  }
}
